/**
 * 单据列表数据服务
 * 提供统一的单据数据获取接口，保证参数构建逻辑一致性
 */
import { app as api } from '@ekuaibao/whispered'
import { QuerySelect } from 'ekbc-query-builder'
import { enableHidingFinishedBills } from '../../../lib/featbit'
import { Fetch } from '@ekuaibao/fetch'
import BillData from '../parts/left-part/MyBillListData'
import { getBillFilterConfig } from '../util/billFilterStorage'

const getUserId = () => {
  try {
    return Fetch?.staffSetting?.id || Fetch?.staffId || api.getState('@common')?.userinfo?.staff?.id
  } catch (error) {
    return Fetch?.staffSetting?.id || Fetch?.staffId
  }
}

/**
 * 从 localStorage 获取用户的筛选配置
 * 现在使用统一的 billFilterStorage 工具
 */
export { getBillFilterConfig } from '../util/billFilterStorage'

/**
 * 构建单据列表查询参数
 * 这个方法与 ListTab.js 中的 fnHandleParams 逻辑完全一致
 */
export const buildBillListParams = ({ filterValue, searchValue, filterTypes, currentSortValue, action }) => {
  let formType =
    !filterValue || filterValue === 'all'
      ? undefined
      : `(form.specificationId.specificationGroupId == "${filterValue}")`

  let filters = ''
  let billTypes = []

  const userId = getUserId()
  const userInfoId = userId ? `${userId}-filter` : 'bill-select-filter'
  const entrustFilter = JSON.parse(localStorage.getItem(userInfoId))?.filter(item => item.type === 'entrust')
  const currentvalue = entrustFilter ? entrustFilter[0]?.children[0]?.type : ''
  let delegatedFilter = currentvalue || 'DELEGATE_TO_ME'

  // 搜索逻辑
  if (searchValue) {
    const useGlobalSearchV2 = window?.PLATFORMINFO?.useGlobalSearchV2
    const isSearchMoney = /^-?\d+(\.\d{0,2})?$/.test(searchValue)
    const lang = Fetch.staffSetting ? Fetch.staffSetting.language : Fetch.defaultLanguage === 'en-US'
    searchValue = JSON.stringify(searchValue)

    if (lang) {
      filters = `form.title.containsIgnoreCase(${searchValue})||form.code.containsIgnoreCase(${searchValue})`
    } else {
      filters = `form.title.containsIgnoreCase(${searchValue})||form.code.containsIgnoreCase(${searchValue})`
    }

    const KA_GLOBAL_SEARCH_2 = api.getState('@common')?.powers?.KA_GLOBAL_SEARCH_2
    if (KA_GLOBAL_SEARCH_2) {
      filters +=
        useGlobalSearchV2 && isSearchMoney
          ? `||form.payMoney.standard.containsIgnoreCase(${searchValue})||form.expenseMoney.standard.containsIgnoreCase(${searchValue})`
          : `||form.containsIgnoreCase(${searchValue})`
    }
    filters = `(${filters})`
  }

  // 筛选类型处理
  if (!!filterTypes?.length) {
    const query = new QuerySelect().filterBy(formType)

    filterTypes.map(el => {
      if (el?.key === 'state') {
        if (!el?.value?.includes('all')) {
          billTypes = el.value
        }
      } else if (el?.key === 'formType') {
        if (enableHidingFinishedBills()) {
          if (!el?.value?.includes('all')) {
            query.filterBy(`formType.in(${el.value.map(t => `"${t}"`).join(',')})`)
            return
          }
        }
        if (!el?.value?.includes('all')) {
          query.filterBy(`formType == "${el.value.join()}"`)
        }
      } else if (el?.key === 'entrust') {
        delegatedFilter = el.value.join()
      }
    })
    formType = query.value().filterBy
  }

  return {
    formType,
    filters,
    billTypes,
    delegatedFilter,
    orderBy: currentSortValue,
    lastAction: action
  }
}

/**
 * 单据列表服务类
 * 提供统一的数据获取接口
 */
class BillListService {
  constructor() {
    this.billData = new BillData()
  }

  /**
   * 获取单据列表（使用当前用户的筛选配置）
   *
   * @param {Object} customConfig - 自定义配置（可选）
   *   用于覆盖从 localStorage 读取的用户配置
   *   这些配置会通过 buildBillListParams 转换为 API 参数
   * @param {string} customConfig.filterValue - 自定义筛选值（覆盖用户保存的 filterValue）
   * @param {string} customConfig.searchValue - 自定义搜索值（覆盖用户保存的 searchValue）
   * @param {Array} customConfig.filterTypes - 自定义筛选类型（覆盖用户保存的 filterTypes）
   * @param {Object} customConfig.currentSortValue - 自定义排序值（覆盖用户保存的 currentSortValue）
   * @param {string} customConfig.action - 操作类型
   *
   * @param {Object} extraParams - 额外的 API 参数（可选）
   *   这些参数会直接传递给 API，不经过 buildBillListParams 转换
   *   对应 ListTab 中 getParams(extraParams) 的 extraParams
   * @param {boolean} extraParams.isLoadMore - 是否加载更多
   * @param {number} extraParams.pageSize - 每页数量
   * @param {*} extraParams.* - 其他 API 参数
   *
   * @returns {Promise<Array>} 单据列表
   *
   * @example
   * // 使用默认配置
   * const billList = await BillListService.getBillList()
   *
   * @example
   * // 覆盖搜索值
   * const billList = await BillListService.getBillList({ searchValue: '报销' })
   *
   * @example
   * // 传递额外的 API 参数
   * const billList = await BillListService.getBillList({}, { isLoadMore: true })
   *
   * @example
   * // 同时覆盖配置和传递额外参数
   * const billList = await BillListService.getBillList(
   *   { searchValue: '报销' },
   *   { isLoadMore: true }
   * )
   */
  async getBillList(customConfig = {}, extraParams = {}, options = {}) {
    if (!options.skipUserInfoLoad) {
      await api.dataLoader('@common.userinfo').load({ type: 'check' })
    }
    // 从 localStorage 获取用户配置
    const userConfig = getBillFilterConfig()

    // 合并用户配置和自定义配置（自定义配置优先）
    const config = {
      ...userConfig,
      ...customConfig,
      // 如果有 searchValue，自动 trim（与 ListTab 保持一致）
      searchValue: (customConfig.searchValue || userConfig.searchValue || '').trim()
    }

    // 构建查询参数（通过 buildBillListParams 转换）
    const params = buildBillListParams(config)

    // 获取数据（合并额外的 API 参数）
    return this.billData.getBills({
      ...params,
      ...extraParams
    })
  }

  /**
   * 使用自定义参数获取单据列表
   * @param {Object} params - 完整的查询参数
   * @returns {Promise<Array>} 单据列表
   */
  async getBillListWithParams(params) {
    return this.billData.getBills(params)
  }

  /**
   * 重置数据
   */
  reset() {
    this.billData.init()
  }

  /**
   * 创建新的 BillData 实例（用于独立的数据管理）
   */
  createBillDataInstance() {
    return new BillData()
  }
}

// 导出单例
export default new BillListService()

// 也导出类，以便需要时创建新实例
export { BillListService }
